import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react" // For loading spinner
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-xl text-sm font-medium ring-offset-white transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 dark:ring-offset-neutral-950 dark:focus-visible:ring-neutral-300",
  {
    variants: {
      variant: {
        default: "bg-ud-orange-75 text-neutral-50 hover:bg-ud-orange dark:bg-ud-dm-orange-75 dark:text-neutral-50 dark:hover:bg-ud-dm-orange active:scale-95",
        destructive:
          "bg-ud-red-75 text-neutral-50 hover:bg-ud-red dark:bg-ud-dm-red-75 dark:text-neutral-50 dark:hover:bg-ud-dm-red active:scale-95",
        outline:
          "border-2 border-ud-orange text-ud-orange hover:bg-ud-orange hover:text-ud-g5 dark:border-ud-dm-orange dark:text-ud-dm-orange dark:hover:bg-ud-dm-orange dark:hover:text-ud-g5 active:scale-95",
        outlineWithDescription:
          "shadow-md border-2 border-ud-orange text-ud-orange hover:bg-ud-orange/25 dark:border-ud-dm-orange dark:text-ud-dm-orange dark:hover:bg-ud-dm-orange dark:hover:text-ud-g5 active:scale-95 w-full flex flex-col items-start text-left",
        secondary:
          "bg-ud-g4-50 text-ud-g1 hover:bg-ud-g4-75 dark:bg-ud-g2-50 dark:text-ud-g5 dark:hover:bg-ud-g2-75 active:scale-95",
        ghost: "text-ud-orange hover:bg-ud-orange-25 dark:text-ud-dm-orange dark:hover:bg-ud-dm-orange-25 hover:text-ud-orange dark:hover:text-ud-dm-orange active:scale-95",
        opaque_ghost: "text-ud-orange opacity-50 hover:bg-ud-orange-25 dark:text-ud-dm-orange dark:hover:bg-ud-dm-orange-25 hover:text-ud-orange dark:hover:text-ud-dm-orange active:scale-95",
        link: "text-ud-orange underline-offset-4 hover:underline dark:text-ud-dm-orange",
        gradient: "bg-gradient-to-r from-ud-orange to-ud-purple text-ud-g5 hover:opacity-90 dark:from-ud-dm-orange dark:to-ud-dm-purple active:scale-95",
      },
      size: {
        dynamic: "h-auto rounded-xl text-base py-2 px-2",
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-xl px-3 text-xs",
        lg: "h-11 rounded-xl px-8 text-base",
        icon: "h-10 w-10",
        iconsm: "h-6 w-6 min-w-6 min-h-6",
        xl: "h-12 rounded-xl px-10 text-lg",
      },
      rounded: {
        default: "rounded-xl",
        full: "rounded-full",
        none: "rounded-none",
      },
      shadow: {
        none: "",
        sm: "shadow-sm",
        md: "shadow-md",
        lg: "shadow-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      rounded: "default",
      shadow: "none",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, rounded, shadow, asChild = false, isLoading, leftIcon, rightIcon, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded, shadow, className }))}
        ref={ref}
        disabled={isLoading || disabled}
        {...props}
      >
        {isLoading && (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        )}
        {!isLoading && leftIcon}
        {children}
        {!isLoading && rightIcon}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }